import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Banknote,
  BarChart3,
  CalendarDays,
  ChevronLeft,
  CreditCard,
  Crown,
  FileText,
  Globe,
  History,
  Image as ImageIcon,
  LayoutDashboard,
  Menu as MenuIcon,
  Plug,
  Plus,
  ShoppingBag,
  SlidersHorizontal,
  Store,
  Tag,
  UserCog,
  UtensilsCrossed,
  Wrench,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { Badge } from '@/shared/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { useAuthStore } from '@/features/auth/store';
import { useRestaurantStore } from '@/features/restaurants/store';
import { restaurantApi } from '@/features/restaurants/api';
import type { RestaurantResponse } from '@/features/restaurants/types';
import { useSubscription } from '@/features/subscriptions/useSubscription';

export type SidebarMode = 'account' | 'restaurant';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  /** For restaurant items: relative path. For account items: absolute path. */
  path: string;
  /** Treat the item as locked when this gate is false. */
  gate?: 'orders' | 'discounts';
  /** Hide this account nav item when the user has no active subscription. */
  requiresActiveSubscription?: boolean;
  /** Sub-paths that should NOT light up this entry (a more specific sibling owns the deeper route). */
  excludePaths?: string[];
}

interface NavGroup {
  id: string;
  label?: string;
  items: NavItem[];
}

const restaurantGroups: NavGroup[] = [
  {
    id: 'operations',
    label: 'sidebar.group.operations',
    items: [
      { id: 'dashboard', label: 'restaurants.nav.dashboard', icon: LayoutDashboard, path: '' },
      {
        id: 'orders',
        label: 'restaurants.nav.orders',
        icon: ShoppingBag,
        path: '/orders',
        excludePaths: ['/orders/history'],
        gate: 'orders',
      },
      { id: 'orderHistory', label: 'restaurants.nav.orderHistory', icon: History, path: '/orders/history' },
      { id: 'reservations', label: 'restaurants.nav.reservations', icon: CalendarDays, path: '/reservations' },
      { id: 'analytics', label: 'restaurants.nav.analytics', icon: BarChart3, path: '/analytics' },
    ],
  },
  {
    id: 'catalog',
    label: 'sidebar.group.catalog',
    items: [
      { id: 'menu', label: 'restaurants.nav.menu', icon: UtensilsCrossed, path: '/menu' },
      { id: 'categories', label: 'restaurants.nav.categories', icon: MenuIcon, path: '/categories' },
      { id: 'addons', label: 'restaurants.nav.addons', icon: Wrench, path: '/addons' },
      { id: 'discounts', label: 'restaurants.nav.discounts', icon: Tag, path: '/discounts', gate: 'discounts' },
    ],
  },
  {
    id: 'configure',
    label: 'sidebar.group.configure',
    items: [
      { id: 'settings', label: 'restaurants.nav.settings', icon: SlidersHorizontal, path: '/settings', excludePaths: ['/settings/media'] },
      { id: 'media', label: 'restaurants.nav.media', icon: ImageIcon, path: '/settings/media' },
      { id: 'integrations', label: 'restaurants.nav.integrations', icon: Plug, path: '/integrations' },
      { id: 'profile', label: 'restaurants.nav.publicPage', icon: Globe, path: '/profile' },
      { id: 'legal', label: 'restaurants.nav.legal', icon: FileText, path: '/legal' },
    ],
  },
];

const accountGroups: NavGroup[] = [
  {
    id: 'workspace',
    label: 'sidebar.group.workspace',
    items: [
      { id: 'restaurants', label: 'account.nav.restaurants', icon: Store, path: '/app' },
    ],
  },
  {
    id: 'billing',
    label: 'sidebar.group.billing',
    items: [
      { id: 'subscription', label: 'account.nav.subscription', icon: Crown, path: '/app/billing' },
      { id: 'payments', label: 'account.nav.payments', icon: CreditCard, path: '/app/settings/payments', excludePaths: ['/app/settings/payments/payouts'], requiresActiveSubscription: true },
      { id: 'payouts', label: 'account.nav.payouts', icon: Banknote, path: '/app/settings/payments/payouts', requiresActiveSubscription: true },
    ],
  },
  {
    id: 'account',
    label: 'sidebar.group.account',
    items: [
      { id: 'settings', label: 'account.nav.settings', icon: UserCog, path: '/app/settings', excludePaths: ['/app/settings/payments'] },
    ],
  },
];

function buildHref(item: NavItem, mode: SidebarMode, restaurantId?: string, locked?: boolean): string {
  if (locked) return '/app/billing';
  if (mode === 'account') return item.path;
  // restaurant mode — relative path under base
  const base = `/app/restaurants/${restaurantId}`;
  if (item.path === '') return base;
  return `${base}${item.path}`;
}

function isItemActive(
  item: NavItem,
  href: string,
  pathname: string,
  mode: SidebarMode,
  restaurantId?: string,
): boolean {
  // Restaurant root (empty relative path) — exact match only
  if (mode === 'restaurant' && item.path === '') {
    return pathname === href || pathname === `${href}/`;
  }
  // Account root (/app) — exact match only so /app/billing doesn't also light it up
  if (mode === 'account' && href === '/app') {
    return pathname === '/app' || pathname === '/app/';
  }
  if (pathname !== href && !pathname.startsWith(`${href}/`)) return false;
  // Honor excludePaths so a more-specific sibling can own a deeper route.
  if (item.excludePaths) {
    if (mode === 'account') {
      return !item.excludePaths.some((ex) => pathname === ex || pathname.startsWith(`${ex}/`));
    }
    if (restaurantId) {
      const base = `/app/restaurants/${restaurantId}`;
      return !item.excludePaths.some((ex) => {
        const exFull = `${base}${ex}`;
        return pathname === exFull || pathname.startsWith(`${exFull}/`);
      });
    }
  }
  return true;
}

function NavItemLink({
  item,
  href,
  active,
  locked,
  collapsed,
  onClick,
}: {
  item: NavItem;
  href: string;
  active: boolean;
  locked: boolean;
  collapsed: boolean;
  onClick?: () => void;
}) {
  const { t } = useTranslation();
  const label = t(item.label);

  const link = (
    <NavLink
      to={href}
      onClick={onClick}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      className={cn(
        'rounded-lg text-sm transition-all duration-200 relative shrink-0',
        collapsed ? 'flex items-center justify-center h-10 w-10' : 'flex items-center gap-3 px-3 py-2',
        locked
          ? 'cursor-pointer text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
          : active
            ? 'bg-accent/[0.10] text-accent dark:bg-accent/[0.15]'
            : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground',
      )}
    >
      <item.icon
        strokeWidth={active ? 2.5 : 1.75}
        className={cn(
          'shrink-0 transition-all duration-200',
          collapsed ? 'h-[18px] w-[18px]' : 'h-4 w-4',
          locked && 'opacity-40',
        )}
        style={{
          color: active ? 'hsl(var(--accent))' : locked ? 'hsl(var(--muted-foreground))' : undefined,
        }}
      />
      {!collapsed && active && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[60%] bg-accent rounded-r-full shadow-[0_0_6px_1px_hsl(var(--accent)/0.5)]" />
      )}
      {collapsed && locked && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-purple-500 ring-2 ring-card"
        />
      )}
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{label}</span>
          {locked && (
            <Badge
              variant="outline"
              className="text-[10px] px-1.5 py-0 h-4 border-purple-500/30 text-purple-500 font-medium"
            >
              PRO
            </Badge>
          )}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="flex items-center gap-2">
        <span>{label}</span>
        {locked && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 h-4 border-purple-500/30 text-purple-500 font-medium"
          >
            PRO
          </Badge>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function RestaurantHeader({
  restaurant,
  collapsed,
}: {
  restaurant: RestaurantResponse | undefined;
  collapsed: boolean;
}) {
  const { t } = useTranslation();
  if (collapsed) {
    return (
      <div className="py-3 flex items-center justify-center border-b border-border">
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 ring-1 ring-border">
              {restaurant?.logoUrl ? (
                <img src={restaurant.logoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <span className="text-sm font-semibold text-muted-foreground">
                    {(restaurant?.name ?? 'R').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <div className="flex items-center gap-2">
              <span>{restaurant?.name || t('app.restaurantFallback')}</span>
              <span
                className={cn(
                  'w-2 h-2 rounded-full',
                  restaurant?.isStoreOpen ? 'bg-success' : 'bg-muted-foreground',
                )}
              />
              <span className="text-xs text-muted-foreground">
                {restaurant?.isStoreOpen ? t('app.open') : t('app.closed')}
              </span>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="relative">
      {restaurant?.coverImageUrl ? (
        <div className="h-24 w-full overflow-hidden">
          <img src={restaurant.coverImageUrl} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
        </div>
      ) : (
        <div className="h-16 w-full bg-gradient-to-br from-accent/20 to-accent/5" />
      )}

      <div className={cn('px-4 pb-4', restaurant?.coverImageUrl ? '-mt-8 relative' : 'pt-4')}>
        <div className="flex items-end gap-3">
          <div className="w-14 h-14 rounded-xl bg-card border-2 border-background shadow-lg overflow-hidden shrink-0">
            {restaurant?.logoUrl ? (
              <img src={restaurant.logoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-accent/10 flex items-center justify-center">
                <Store className="h-6 w-6 text-accent" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 pb-1">
            <h2 className="font-semibold text-sm truncate">{restaurant?.name || 'Restaurant'}</h2>
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  'w-2 h-2 rounded-full',
                  restaurant?.isStoreOpen ? 'bg-success' : 'bg-muted-foreground',
                )}
              />
              <span className="text-xs text-muted-foreground">
                {restaurant?.isStoreOpen ? t('app.open') : t('app.closed')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountHeader({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const initial = (user?.name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase();

  if (collapsed) {
    return (
      <div className="py-3 flex items-center justify-center border-b border-border">
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/10 flex items-center justify-center">
              <span className="text-sm font-bold text-accent">{initial}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {user?.name || user?.email || t('sidebar.account')}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="px-4 pt-5 pb-4 border-b border-border">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/10 flex items-center justify-center shrink-0">
          <span className="text-base font-bold text-accent">{initial}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{user?.name || t('sidebar.account')}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
      </div>
    </div>
  );
}

interface AppSidebarProps {
  mode: SidebarMode;
  collapsed: boolean;
  onNavigate?: () => void;
}

export function AppSidebar({ mode, collapsed, onNavigate }: AppSidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const token = useAuthStore((s) => s.token);
  const { canAcceptOrders, canUseDiscounts, isActive: hasActiveSubscription } = useSubscription();

  const { data: restaurant } = useQuery<RestaurantResponse>({
    queryKey: ['restaurant', restaurantId],
    queryFn: () => restaurantApi.getRestaurant(token, restaurantId!),
    enabled: !!token && !!restaurantId && mode === 'restaurant',
  });

  const isLocked = (item: NavItem) => {
    if (mode !== 'restaurant') return false;
    if (item.gate === 'orders') return !canAcceptOrders;
    if (item.gate === 'discounts') return !canUseDiscounts;
    return false;
  };

  const groups = mode === 'restaurant' ? restaurantGroups : accountGroups;

  return (
    <div className="flex flex-col h-full min-h-0">
      {mode === 'restaurant' ? (
        <RestaurantHeader restaurant={restaurant} collapsed={collapsed} />
      ) : (
        <AccountHeader collapsed={collapsed} />
      )}

      <div className={cn('flex-1 overflow-y-auto overflow-x-hidden overscroll-contain [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]', collapsed ? 'px-0' : 'px-3')}>
        <nav
          className={cn(
            'flex flex-col',
            collapsed ? 'items-center py-3 gap-2' : 'py-4 space-y-6',
          )}
        >
          {groups.map((group, groupIdx) => (
            <div key={group.id} className="w-full flex flex-col">
              {groupIdx > 0 && collapsed && <div className="w-8 h-px bg-border mb-2" aria-hidden />}
              {!collapsed && group.label && (
                <div className="px-3 pb-2 pt-1">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">
                    {t(group.label)}
                  </span>
                </div>
              )}
              <div
                className={cn(
                  'w-full flex flex-col',
                  collapsed ? 'items-center gap-1' : 'space-y-1',
                )}
              >
                {group.items.filter((item) =>
                  !(item.requiresActiveSubscription && !hasActiveSubscription)
                ).map((item) => {
                  const locked = isLocked(item);
                  const href = buildHref(item, mode, restaurantId, locked);
                  const active =
                    !locked && isItemActive(item, href, location.pathname, mode, restaurantId);
                  return (
                    <NavItemLink
                      key={item.id}
                      item={item}
                      href={href}
                      active={active}
                      locked={locked}
                      collapsed={collapsed}
                      onClick={onNavigate}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Footer — restaurant mode shows a "back to all restaurants" shortcut.
          Account mode keeps the rail clean since the user dropdown handles sign-out. */}
      {mode === 'restaurant' && (
        <div
          className={cn(
            'border-t border-border',
            collapsed ? 'py-2 flex items-center justify-center' : 'p-3',
          )}
        >
          {collapsed ? (
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <NavLink
                  to="/app"
                  onClick={onNavigate}
                  aria-label={t('restaurants.nav.allRestaurants')}
                  className="flex items-center justify-center h-10 w-10 rounded-lg text-foreground hover:bg-secondary transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {t('restaurants.nav.allRestaurants')}
              </TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/app"
              onClick={onNavigate}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{t('restaurants.nav.allRestaurants')}</span>
            </NavLink>
          )}
        </div>
      )}
    </div>
  );
}

interface SidebarToggleProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function SidebarToggle({ collapsed, onToggle }: SidebarToggleProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapsed ? t('app.expandSidebar') : t('app.collapseSidebar')}
      aria-pressed={!collapsed}
      className={cn(
        'absolute -right-3.5 top-5 z-50 group',
        'flex items-center justify-center',
        'h-8 w-8 rounded-full',
        'bg-card border border-border shadow-md',
        'text-foreground',
        'transition-all duration-200',
        'hover:shadow-lg hover:bg-accent hover:text-accent-foreground hover:scale-110 hover:border-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
      )}
    >
      <ChevronLeft
        className={cn('h-4 w-4 transition-transform duration-300', collapsed && 'rotate-180')}
      />
    </button>
  );
}
